<template>
  <div class="v-tabs-mts">
    <div ref="container" class="v-tabs-mts__list-wrapper">
      <ul
        ref="list"
        class="v-tabs-mts__list"
        :class="{ '--dragging': isDragging }"
        :style="{ transform: `translateX(${currentTranslate}px)` }"
        @pointerdown="startDrag"
        @touchstart="startDrag"
      >
        <li
          v-for="(tab, idx) in tabs"
          :key="idx"
          class="v-tabs-mts__list-item"
          :class="{ '--active': tab.isActive }"
          @click="selectTab(tab.name)"
        >
          {{ tab.name }}
        </li>
      </ul>
      <span v-if="showPrev" class="v-tabs-mts__list-control --prev">
        <VIconMTS
          name="mts/icon-mts--arrow-accordion"
          :width="24"
          :height="24"
          fill="#969FA8"
        />
      </span>
      <span v-if="showNext" class="v-tabs-mts__list-control --next">
        <VIconMTS
          name="mts/icon-mts--arrow-accordion"
          :width="24"
          :height="24"
          fill="#969FA8"
        />
      </span>
    </div>

    <div class="v-tabs-mts__content">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import VIconMTS from './VIconMTS.vue'

export default {
  name: 'VTabsMTS',
  components: { VIconMTS },
  props: {
    selectedTab: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      tabs: [],
      isDragging: false,
      startX: 0,
      currentTranslate: 0,
      initialTranslate: 0,
      minTranslate: 0,
      isOverflow: false,
      observer: null,
    }
  },
  computed: {
    showPrev() {
      return this.isOverflow && this.currentTranslate < 0
    },
    showNext() {
      return this.isOverflow && this.currentTranslate > this.minTranslate
    },
  },
  watch: {
    tabs() {
      this.$nextTick(this.updateMetrics)
    },
    selectedTab(newVal) {
      this.selectTab(newVal)
    },
  },
  mounted() {
    this.tabs = this.$children.filter(
      (child) => child.name !== 'mts/icon-mts--arrow-accordion'
    )

    this.observer = new ResizeObserver(this.handleResize)
    this.observer.observe(this.$refs.container)
    this.observer.observe(this.$refs.list)

    this.$nextTick(this.updateMetrics)
  },
  beforeDestroy() {
    this.observer?.disconnect()
  },
  methods: {
    updateMetrics() {
      const container = this.$refs.container
      const list = this.$refs.list
      if (!container || !list) return

      const containerWidth = container.offsetWidth
      const listWidth = list.scrollWidth

      this.isOverflow = listWidth > containerWidth
      this.minTranslate = this.isOverflow
        ? Math.min(0, containerWidth - listWidth)
        : 0

      if (!this.isOverflow) {
        this.currentTranslate = 0
      }
    },
    handleResize() {
      this.updateMetrics()
      this.currentTranslate = Math.max(
        this.minTranslate,
        Math.min(this.currentTranslate, 0)
      )
    },
    selectTab(nameSelectedTab) {
      this.tabs.forEach((tab) => {
        tab.isActive = tab.name === nameSelectedTab
      })
      this.$emit('update:selectedTab', nameSelectedTab)
    },
    startDrag(event) {
      const isTouch = event.type === 'touchstart'
      if (!isTouch && event.button !== 0) return

      this.isDragging = true
      this.startX = isTouch ? event.touches[0].pageX : event.pageX
      this.initialTranslate = this.currentTranslate

      const moveEvent = isTouch ? 'touchmove' : 'mousemove'
      const endEvent = isTouch ? 'touchend' : 'mouseup'

      document.addEventListener(moveEvent, this.duringDrag)
      document.addEventListener(endEvent, this.stopDrag)
    },
    duringDrag(event) {
      if (!this.isDragging) return
      event.preventDefault()

      const isTouch = event.type.startsWith('touch')
      const x = isTouch ? event.touches[0].pageX : event.pageX
      const walk = (x - this.startX) * 1.5
      const newTranslate = this.initialTranslate + walk

      this.currentTranslate = Math.max(
        this.minTranslate,
        Math.min(newTranslate, 0)
      )
    },
    stopDrag() {
      this.isDragging = false
      document.removeEventListener('mousemove', this.duringDrag)
      document.removeEventListener('touchmove', this.duringDrag)
      document.removeEventListener('mouseup', this.stopDrag)
      document.removeEventListener('touchend', this.stopDrag)
    },
  },
}
</script>
<style lang="scss" scoped>
.v-tabs-mts {
  font-family: $font-mts-compact;
  @include screen-up('lg') {
    margin: 32px 0;
  }

  &__list {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    list-style: none;
    column-gap: 24px;
    white-space: nowrap;
    transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    will-change: transform;
    cursor: grab;
    user-select: none;
    &.--dragging {
      cursor: grabbing;
      transition: none;
    }
    &-wrapper {
      overflow: hidden;
      width: 100%;
      position: relative;
    }
    &-control {
      position: absolute;
      top: 0;
      width: 44px;
      height: 52px;
      padding: 16px 10px;
      border-bottom: 1px solid;
      backdrop-filter: blur(16px);
      background: linear-gradient(
        270deg,
        rgba(242, 243, 247, 0.04) 0%,
        rgba(242, 243, 247, 0.4) 100%
      );
      border-image-source: linear-gradient(
        0deg,
        rgba(188, 195, 208, 0.1) 0%,
        rgba(188, 195, 208, 0.5) 48.5%,
        rgba(188, 195, 208, 0.1) 95.5%
      );

      &.--next {
        right: 0;
        left: auto;
        transform: rotate(90deg);
      }
      &.--prev {
        left: 0;
        transform: rotate(-90deg);
      }
    }

    &-item {
      display: block;
      position: relative;
      padding: 12px 0;
      font-size: 20px;
      line-height: 28px;
      color: #969fa8;
      cursor: pointer;
      transition: 0.3s ease-out;

      &::after {
        content: '';
        display: none;
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 4px;
        border-radius: 4px;
        background: #e2e5eb;
        transition: all 0.275s ease;
      }

      &.--dragging {
        cursor: grabbing;
      }

      &.--active {
        color: $color-dark-2;
        &:after {
          display: block;
        }
      }
    }
  }
  &__content {
    margin-top: 32px;
  }
}
</style>
